// Accès Supabase aux rôles employés configurables (table `roles_employe`).
//
// Le scope (agence OU propriétaire) est IMPOSÉ par la RLS : le client fournit
// l'identifiant de son organisation, la base vérifie qu'il correspond bien au
// compte connecté (isolation garantie côté serveur). Aucune donnée fictive :
// les rôles sont créés par le DG lui-même.

import { supabase } from "@/store/shared/supabase";
import type { AccountRole } from "@/session/types";
import type { PermissionMap } from "./catalog";

export interface RoleEmploye {
  id: string;
  code: string;
  nom: string;
  permissions: PermissionMap;
}

export interface OrganisationScope {
  agenceId: string | null;
  proprietaireId: string | null;
}

type Row = Record<string, unknown>;
const str = (value: unknown): string => (typeof value === "string" ? value : "");

function toPermissionMap(value: unknown): PermissionMap {
  if (value && typeof value === "object" && !Array.isArray(value)) return value as PermissionMap;
  return {};
}

/** Vrai si le rôle correspond à l'espace Propriétaire. */
export function isOwnerSpace(role: AccountRole): boolean {
  return role === "dg_proprietaire";
}

/** Identifiant de l'organisation du compte connecté (agence ou propriétaire). */
export async function fetchScopeId(compteId: string, role: AccountRole): Promise<OrganisationScope> {
  if (role === "dg_proprietaire") {
    const { data, error } = await supabase
      .from("proprietaires")
      .select("id")
      .eq("compte_id", compteId)
      .maybeSingle();
    if (error) throw error;
    return { agenceId: null, proprietaireId: (data as Row | null)?.id ? str((data as Row).id) : null };
  }
  const { data, error } = await supabase.from("agences").select("id").eq("compte_id", compteId).maybeSingle();
  if (error) throw error;
  return { agenceId: (data as Row | null)?.id ? str((data as Row).id) : null, proprietaireId: null };
}

/** Rôles configurés de l'organisation connectée (RLS-scopé). */
export async function fetchRolesEmploye(): Promise<RoleEmploye[]> {
  const { data, error } = await supabase
    .from("roles_employe")
    .select("id, code, nom, permissions")
    .order("nom", { ascending: true });
  if (error) throw error;
  return ((data as Row[] | null) ?? []).map((row) => ({
    id: str(row.id),
    code: str(row.code),
    nom: str(row.nom),
    permissions: toPermissionMap(row.permissions),
  }));
}

function slugify(nom: string): string {
  const base = nom
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .slice(0, 40);
  return base || "role";
}

/** Crée un rôle appartenant à l'organisation connectée. */
export async function createRoleEmploye(
  scope: OrganisationScope,
  nom: string,
  permissions: PermissionMap
): Promise<void> {
  const existing = await fetchRolesEmploye();
  const codes = new Set(existing.map((role) => role.code));
  const base = slugify(nom);
  let code = base;
  let index = 2;
  while (codes.has(code)) {
    code = `${base}_${index}`;
    index += 1;
  }
  const { error } = await supabase.from("roles_employe").insert({
    agence_id: scope.agenceId,
    proprietaire_id: scope.proprietaireId,
    code,
    nom: nom.trim(),
    permissions,
  });
  if (error) throw error;
}

/** Comparaison canonique (indépendante de l'ordre des clés) de deux permissions. */
function canonicalPermissions(map: PermissionMap | null | undefined): string {
  const source = map ?? {};
  const modules = Object.keys(source).sort();
  return JSON.stringify(
    modules.map((module) => {
      const actions = source[module] ?? {};
      const actionKeys = Object.keys(actions).sort();
      return [module, actionKeys.map((action) => [action, Boolean(actions[action as keyof typeof actions])])];
    })
  );
}

function samePermissions(a: PermissionMap | null | undefined, b: PermissionMap | null | undefined): boolean {
  return canonicalPermissions(a) === canonicalPermissions(b);
}

/**
 * Met à jour un rôle et propage ses nouvelles permissions aux employés qui
 * l'utilisent ET qui n'avaient pas été réduits individuellement (leurs
 * permissions correspondaient exactement au modèle précédent du rôle).
 * Renvoie le nombre d'employés réellement mis à jour.
 */
export async function updateRoleEmploye(
  id: string,
  code: string,
  nom: string,
  permissions: PermissionMap,
  previousPermissions: PermissionMap
): Promise<number> {
  const { error } = await supabase
    .from("roles_employe")
    .update({ nom: nom.trim(), permissions })
    .eq("id", id);
  if (error) throw error;

  const { data, error: employesError } = await supabase
    .from("employes_agence")
    .select("id, permissions")
    .eq("fonction", code);
  if (employesError) throw employesError;

  const rows = (data as Row[] | null) ?? [];
  let updated = 0;
  for (const row of rows) {
    const current = toPermissionMap(row.permissions);
    if (samePermissions(current, previousPermissions)) {
      const { error: updateError } = await supabase
        .from("employes_agence")
        .update({ permissions, updated_at: new Date().toISOString() })
        .eq("id", str(row.id));
      if (updateError) throw updateError;
      updated += 1;
    }
  }
  return updated;
}

/** Nombre d'employés de l'organisation utilisant ce code de rôle. */
export async function countEmployesForRole(code: string): Promise<number> {
  const { count, error } = await supabase
    .from("employes_agence")
    .select("id", { count: "exact", head: true })
    .eq("fonction", code);
  if (error) throw error;
  return count ?? 0;
}

/** Supprime un rôle non utilisé par un employé de l'organisation. */
export async function deleteRoleEmploye(id: string, code: string): Promise<void> {
  const used = await countEmployesForRole(code);
  if (used > 0) {
    throw new Error(
      `Ce rôle est utilisé par ${used} employé${used > 1 ? "s" : ""}. Réaffectez-les avant de le supprimer.`
    );
  }
  const { error } = await supabase.from("roles_employe").delete().eq("id", id);
  if (error) throw error;
}